import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Add, MusicNote, Edit, Delete, DragIndicator, Close, GridView, ViewList,
} from '@mui/icons-material';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, rectSortingStrategy, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { useOrientation } from '../../hooks/useOrientation';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import {
  SpotifyPlaylist, getSpotifyPlaylists, createSpotifyPlaylist, deleteSpotifyPlaylist,
  updateSpotifyPlaylist, getSpotifyPlaylistSort, putSpotifyPlaylistSort,
  getSpotifyPlaylistTracks, extractSpotifyPlaylistTrackItems,
  reorderSpotifyPlaylistTracks, removeTracksFromSpotifyPlaylist,
  PlaylistSortMode, PlaylistViewMode,
  playlistTrackIndexMatchesFilter,
} from '../../services/sonosApi';
import { CARD_GLOW_BG, SONOS_BLUE, SURFACE, SURFACE_BORDER, sonosMenuPaperSx, sonosSelectFieldSx } from './sonosUiStyles';

const CARD_BG = SURFACE;
const BLUE = SONOS_BLUE;
/** Mosaicos Reproducción: 2 columnas, tile más chico con aire entre ítems */
const SPOTIFY_TILE_WIDTH = '86%';
const SPOTIFY_GRID_COLUMN_GAP = 2;
const SPOTIFY_GRID_ROW_GAP = 2.25;

interface SortablePlaylistProps {
  id: string;
  playlist: SpotifyPlaylist;
  selected: boolean;
  sortManual: boolean;
  viewMode: PlaylistViewMode;
  variant: 'classic' | 'spotify';
  onSelect: () => void;
  onPlay: () => void;
  onOpen?: () => void;
  onEdit: () => void;
}

const SortablePlaylist: React.FC<SortablePlaylistProps> = ({
  id, playlist, selected, sortManual, viewMode, variant, onSelect, onPlay, onOpen, onEdit,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !sortManual });
  const dragHandle = sortManual ? (
    <IconButton
      size="small"
      sx={{ color: '#666', p: 0, flexShrink: 0, cursor: 'grab' }}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    >
      <DragIndicator sx={{ fontSize: 16 }} />
    </IconButton>
  ) : null;

  const openPlaylist = variant === 'spotify' ? onOpen : undefined;
  const handleTileClick = () => {
    if (variant === 'spotify' && openPlaylist) {
      openPlaylist();
      return;
    }
    onPlay();
  };
  const handleListClick = () => {
    if (variant === 'spotify' && openPlaylist) {
      openPlaylist();
      return;
    }
    onSelect();
  };

  if (viewMode === 'grid' && variant === 'spotify') {
    return (
      <Box
        ref={setNodeRef}
        style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
        onClick={handleTileClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          minWidth: 0,
          width: SPOTIFY_TILE_WIDTH,
          maxWidth: SPOTIFY_TILE_WIDTH,
          mx: 'auto',
          justifySelf: 'center',
          touchAction: 'pan-y',
          position: 'relative',
        }}
      >
        {sortManual && (
          <IconButton
            size="small"
            sx={{ position: 'absolute', top: 2, left: 2, color: '#ccc', p: 0.25, zIndex: 1, cursor: 'grab', bgcolor: 'rgba(0,0,0,0.45)' }}
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIndicator sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        <Box sx={{
          width: '100%',
          aspectRatio: '1',
          borderRadius: 2,
          background: CARD_GLOW_BG,
          backgroundImage: playlist.image_url ? `url(${playlist.image_url})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}>
          {!playlist.image_url && <MusicNote sx={{ color: '#555', fontSize: 32 }} />}
        </Box>
        <Typography sx={{
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.25,
          mt: 0.65,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {playlist.name}
        </Typography>
      </Box>
    );
  }

  if (viewMode === 'list') {
    const listRowSx = variant === 'spotify' ? {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      mb: 0,
      cursor: 'pointer',
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      minWidth: 0,
      width: '100%',
      boxSizing: 'border-box' as const,
      touchAction: 'pan-y' as const,
      backgroundColor: 'transparent',
      borderRadius: 0,
      border: 'none',
      px: 0,
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
    } : {
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 0.75,
      py: 0.5,
      mb: 0.5,
      backgroundColor: selected ? 'rgba(35,145,255,0.08)' : CARD_BG,
      borderRadius: '12px',
      cursor: 'pointer',
      border: selected ? `1px solid ${BLUE}` : `1px solid ${SURFACE_BORDER}`,
      minWidth: 0,
      width: '100%',
      boxSizing: 'border-box' as const,
      touchAction: 'pan-y' as const,
      '&:hover': { filter: 'brightness(1.1)' },
    };

    return (
      <Box
        ref={setNodeRef}
        style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
        onClick={variant === 'spotify' ? handleListClick : onSelect}
        sx={listRowSx}
      >
        {dragHandle}
        <Box sx={{
          width: variant === 'spotify' ? 44 : 36,
          height: variant === 'spotify' ? 44 : 36,
          borderRadius: variant === 'spotify' ? 1 : 0.75,
          background: CARD_GLOW_BG,
          backgroundImage: playlist.image_url ? `url(${playlist.image_url})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {!playlist.image_url && <MusicNote sx={{ color: '#555', fontSize: variant === 'spotify' ? 20 : 18 }} />}
        </Box>
        <Typography sx={{
          color: '#fff',
          fontSize: variant === 'spotify' ? 13 : 12,
          fontWeight: variant === 'spotify' ? 600 : 400,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {playlist.name}
        </Typography>
        {variant !== 'spotify' && (
          <IconButton
            size="small"
            sx={{ color: '#999', p: 0.25, flexShrink: 0 }}
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
          >
            <Edit sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      onClick={onSelect}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: selected ? '#323840' : 'transparent',
        borderRadius: 1,
        p: 0.5,
        cursor: 'pointer',
        position: 'relative',
        border: selected ? `1px solid ${BLUE}` : '1px solid transparent',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
        textAlign: 'center',
        touchAction: 'pan-y',
      }}
    >
      {sortManual && (
        <IconButton
          size="small"
          sx={{ position: 'absolute', top: 0, left: 0, color: '#666', p: 0, zIndex: 1, cursor: 'grab' }}
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIndicator sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      <IconButton
        size="small"
        sx={{ position: 'absolute', top: 0, right: 0, color: '#888', p: 0, zIndex: 1 }}
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
      >
        <Edit sx={{ fontSize: 13 }} />
      </IconButton>
      <Box sx={{
        width: '100%',
        aspectRatio: '1',
        maxHeight: 76,
        borderRadius: 1,
        backgroundColor: '#1a1c1e',
        backgroundImage: playlist.image_url ? `url(${playlist.image_url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 0.35,
      }}>
        {!playlist.image_url && <MusicNote sx={{ color: '#555', fontSize: 22 }} />}
      </Box>
      <Typography sx={{
        color: '#fff',
        fontSize: 11,
        lineHeight: 1.25,
        width: '100%',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        px: 0.25,
      }}>
        {playlist.name}
      </Typography>
    </Box>
  );
};

interface SpotifyPlaylistGridProps {
  onPlay: (pl: SpotifyPlaylist) => void;
  onOpenPlaylist?: (pl: SpotifyPlaylist) => void;
  onSelect?: (pl: SpotifyPlaylist | null) => void;
  selectedUri?: string | null;
  variant?: 'classic' | 'spotify';
  sortMode?: PlaylistSortMode;
  onSortModeChange?: (mode: PlaylistSortMode) => void;
  viewMode?: PlaylistViewMode;
  onViewModeChange?: (mode: PlaylistViewMode) => void;
  showToolbar?: boolean;
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
  filterQuery?: string;
  refreshKey?: number;
  /** Fija columnas de grilla (p. ej. 2 en modal de parlante). Sin esto: spotify 5/2, classic 4/3 según orientación. */
  columns?: number;
}

const SpotifyPlaylistGrid: React.FC<SpotifyPlaylistGridProps> = ({
  onPlay, onOpenPlaylist, onSelect, selectedUri, variant = 'classic',
  sortMode: sortModeProp, onSortModeChange,
  viewMode: viewModeProp, onViewModeChange,
  showToolbar = true,
  createOpen: createOpenProp, onCreateOpenChange,
  filterQuery = '',
  refreshKey = 0,
  columns,
}) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [internalSortMode, setInternalSortMode] = useState<PlaylistSortMode>('alpha');
  const [internalViewMode, setInternalViewMode] = useState<PlaylistViewMode>('grid');
  const sortMode = sortModeProp ?? internalSortMode;
  const setSortMode = onSortModeChange ?? setInternalSortMode;
  const viewMode = viewModeProp ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;
  const [manualOrder, setManualOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [internalCreateOpen, setInternalCreateOpen] = useState(false);
  const createOpen = createOpenProp ?? internalCreateOpen;
  const setCreateOpen = onCreateOpenChange ?? setInternalCreateOpen;
  const [newName, setNewName] = useState('');
  const [detailPl, setDetailPl] = useState<SpotifyPlaylist | null>(null);
  const [detailTracks, setDetailTracks] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);

  const closeCreateDialog = useCallback(() => {
    setCreateOpen(false);
    setCreateError(null);
  }, [setCreateOpen]);

  const closeDetailDialog = useCallback(() => {
    setDetailPl(null);
  }, []);

  const requestCloseCreate = useOverlayBackButton(createOpen, closeCreateDialog, 'sonos-playlist-create');
  const requestCloseDetail = useOverlayBackButton(Boolean(detailPl), closeDetailDialog, 'sonos-playlist-detail-edit');

  const gridColumns = columns ?? (variant === 'spotify'
    ? (isHorizontal ? 5 : 2)
    : (isHorizontal ? 4 : 3));

  const load = useCallback(async () => {
    setLoading(true);
    setRateLimited(false);
    let limited = false;
    try {
      const [pls, order] = await Promise.all([
        getSpotifyPlaylists().catch((e: any) => {
          if (e?.response?.status === 429) limited = true;
          return [];
        }),
        getSpotifyPlaylistSort().catch(() => []),
      ]);
      setPlaylists(pls);
      setManualOrder(order.length ? order : pls.map((p) => p.uri));
      setRateLimited(limited);
    } catch {
      setPlaylists([]);
      setManualOrder([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const sorted = useMemo(() => {
    if (sortMode === 'alpha') return [...playlists].sort((a, b) => a.name.localeCompare(b.name));
    if (sortMode === 'alpha_desc') return [...playlists].sort((a, b) => b.name.localeCompare(a.name));
    if (sortMode === 'added_new') {
      return [...playlists].sort((a, b) => {
        const ta = a.added_at ? Date.parse(a.added_at) : 0;
        const tb = b.added_at ? Date.parse(b.added_at) : 0;
        return tb - ta;
      });
    }
    if (sortMode === 'added_old') {
      return [...playlists].sort((a, b) => {
        const ta = a.added_at ? Date.parse(a.added_at) : Number.MAX_SAFE_INTEGER;
        const tb = b.added_at ? Date.parse(b.added_at) : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
    }
    if (sortMode === 'manual') {
      const map = new Map(playlists.map((p) => [p.uri, p]));
      const ordered = manualOrder.map((uri) => map.get(uri)).filter(Boolean) as SpotifyPlaylist[];
      playlists.forEach((p) => { if (!manualOrder.includes(p.uri)) ordered.push(p); });
      return ordered;
    }
    return playlists;
  }, [playlists, sortMode, manualOrder]);

  const filtered = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((p) => {
      const name = p.name?.toLowerCase() ?? '';
      const owner = p.owner?.toLowerCase() ?? '';
      if (name.includes(q) || owner.includes(q)) return true;
      return playlistTrackIndexMatchesFilter(p.id, q);
    });
  }, [sorted, filterQuery]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 8 } }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const uris = sorted.map((p) => p.uri);
    const oldIndex = uris.indexOf(String(active.id));
    const newIndex = uris.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(uris, oldIndex, newIndex);
    setManualOrder(next);
    try {
      await putSpotifyPlaylistSort(next);
    } catch { /* noop */ }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreateError(null);
    try {
      await createSpotifyPlaylist({ name: newName.trim() });
      setCreateOpen(false);
      setNewName('');
      try {
        await load();
      } catch {
        setCreateError(t('musicSystem.playlists.reloadError'));
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setCreateError(typeof detail === 'string' ? detail : t('musicSystem.playlists.createError'));
    }
  };

  const openDetail = async (pl: SpotifyPlaylist) => {
    setDetailPl(pl);
    setRenameName(pl.name);
    setDetailLoading(true);
    try {
      const data = await getSpotifyPlaylistTracks(pl.id);
      setDetailTracks(extractSpotifyPlaylistTrackItems(data));
    } catch {
      setDetailTracks([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeletePlaylist = async () => {
    if (!detailPl || !window.confirm(t('musicSystem.playlists.deleteConfirm'))) return;
    await deleteSpotifyPlaylist(detailPl.id);
    setDetailPl(null);
    if (selectedUri === detailPl.uri) onSelect?.(null);
    load();
  };

  const handleRename = async () => {
    if (!detailPl || !renameName.trim()) return;
    await updateSpotifyPlaylist(detailPl.id, { name: renameName.trim() });
    setDetailPl({ ...detailPl, name: renameName.trim() });
    load();
  };

  const handleRemoveTrack = async (index: number, uri: string) => {
    if (!detailPl) return;
    await removeTracksFromSpotifyPlaylist(detailPl.id, [uri]);
    setDetailTracks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTrackDragEnd = async (event: DragEndEvent) => {
    if (!detailPl) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = detailTracks.findIndex((_, i) => `t-${i}` === active.id);
    const newIndex = detailTracks.findIndex((_, i) => `t-${i}` === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(detailTracks, oldIndex, newIndex);
    setDetailTracks(next);
    try {
      await reorderSpotifyPlaylistTracks(detailPl.id, oldIndex, newIndex);
    } catch {
      openDetail(detailPl);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress sx={{ color: BLUE }} /></Box>;
  }

  const playlistItems = filtered.map((pl) => (
    <SortablePlaylist
      key={pl.uri}
      id={pl.uri}
      playlist={pl}
      selected={selectedUri === pl.uri}
      sortManual={sortMode === 'manual'}
      viewMode={viewMode}
      variant={variant}
      onSelect={() => { onSelect?.(pl); }}
      onPlay={() => onPlay(pl)}
      onOpen={onOpenPlaylist ? () => onOpenPlaylist(pl) : undefined}
      onEdit={() => openDetail(pl)}
    />
  ));

  const playlistContent = viewMode === 'grid' ? (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
      columnGap: variant === 'spotify' ? SPOTIFY_GRID_COLUMN_GAP : 0.75,
      rowGap: variant === 'spotify' ? SPOTIFY_GRID_ROW_GAP : 0.75,
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      pb: 0.5,
    }}>
      {playlistItems}
    </Box>
  ) : (
    <Box sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      {playlistItems}
    </Box>
  );

  const sortedContent = sortMode === 'manual' && !filterQuery.trim() ? (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={filtered.map((p) => p.uri)}
        strategy={viewMode === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
      >
        {playlistContent}
      </SortableContext>
    </DndContext>
  ) : playlistContent;

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
      {showToolbar && variant !== 'spotify' && (
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        mb: 0.75,
        alignItems: 'center',
        flexWrap: 'nowrap',
        width: '100%',
        minWidth: 0,
        flexShrink: 0,
      }}>
        <FormControl size="small" sx={{ ...sonosSelectFieldSx, minWidth: 0, flex: '1 1 auto', maxWidth: 150 }}>
          <InputLabel>{t('musicSystem.playlists.sort')}</InputLabel>
          <Select
            value={sortMode}
            label={t('musicSystem.playlists.sort')}
            onChange={(e) => setSortMode(e.target.value as PlaylistSortMode)}
            MenuProps={{ PaperProps: { sx: sonosMenuPaperSx } }}
          >
            <MenuItem value="alpha">{t('musicSystem.playlists.sortAlpha')}</MenuItem>
            <MenuItem value="alpha_desc">{t('musicSystem.playlists.sortAlphaDesc')}</MenuItem>
            <MenuItem value="manual">{t('musicSystem.playlists.sortManual')}</MenuItem>
            <MenuItem value="added_new">{t('musicSystem.playlists.sortAddedNew')}</MenuItem>
            <MenuItem value="added_old">{t('musicSystem.playlists.sortAddedOld')}</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title={t('musicSystem.playlists.new')}>
          <IconButton size="small" onClick={() => setCreateOpen(true)} sx={{ color: BLUE, p: 0.5, flexShrink: 0, border: '1px solid #333', borderRadius: 1 }}>
            <Add sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={viewMode === 'grid' ? t('musicSystem.playlists.viewList') : t('musicSystem.playlists.viewGrid')}>
          <IconButton
            size="small"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            sx={{ color: BLUE, p: 0.5, flexShrink: 0, border: '1px solid #333', borderRadius: 1 }}
          >
            {viewMode === 'grid' ? <ViewList sx={{ fontSize: 18 }} /> : <GridView sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>
      )}

      {rateLimited && playlists.length === 0 ? (
        <Box sx={{ py: 2, px: 0.5, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
          <Typography sx={{ color: '#e0b34a', fontSize: 12 }}>
            {t('musicSystem.playlists.rateLimited')}
          </Typography>
          <Button size="small" onClick={() => { void load(); }} sx={{ color: BLUE, textTransform: 'none' }}>
            {t('musicSystem.playlists.retry')}
          </Button>
        </Box>
      ) : playlists.length === 0 ? (
        <Typography sx={{ color: '#999', fontSize: 12 }}>{t('musicSystem.playback.noPlaylists')}</Typography>
      ) : filtered.length === 0 ? (
        <Typography sx={{ color: '#999', fontSize: 12 }}>{t('musicSystem.playlists.noFilterResults')}</Typography>
      ) : sortedContent}

      <Dialog open={createOpen} onClose={requestCloseCreate} PaperProps={{ sx: { bgcolor: 'rgba(18,18,20,0.98)', color: '#fff', border: `1px solid ${SURFACE_BORDER}`, borderRadius: '16px' } }}>
        <DialogTitle>{t('musicSystem.playlists.new')}</DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} sx={{ mt: 1, input: { color: '#fff' } }} />
          {createError && (
            <Typography sx={{ color: '#e57373', fontSize: 11, mt: 1 }}>{createError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={requestCloseCreate} sx={{ color: '#999' }}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} sx={{ color: BLUE }}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detailPl)} onClose={requestCloseDetail} fullWidth maxWidth="sm" PaperProps={{ sx: { bgcolor: '#1a1a1a', color: '#fff' } }}>
        {detailPl && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <TextField size="small" value={renameName} onChange={(e) => setRenameName(e.target.value)} sx={{ flex: 1, mr: 1, input: { color: '#fff' } }} />
              <IconButton onClick={handleDeletePlaylist} sx={{ color: '#e57373' }}><Delete /></IconButton>
            </DialogTitle>
            <DialogContent>
              <Button size="small" onClick={handleRename} sx={{ color: BLUE, mb: 1, textTransform: 'none' }}>{t('musicSystem.playlists.saveName')}</Button>
              {detailLoading ? (
                <CircularProgress sx={{ color: BLUE }} />
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTrackDragEnd}>
                  <SortableContext items={detailTracks.map((_, i) => `t-${i}`)} strategy={rectSortingStrategy}>
                    {detailTracks.map((tr, i) => {
                      const track = tr.track || tr.item || tr;
                      const uri = track.uri;
                      return (
                        <TrackRow key={`t-${i}`} id={`t-${i}`} name={track.name} artist={track.artists?.map((a: any) => a.name).join(', ')} onRemove={() => handleRemoveTrack(i, uri)} />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

const TrackRow: React.FC<{ id: string; name: string; artist?: string; onRemove: () => void }> = ({ id, name, artist, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  return (
    <Box ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 0.5, backgroundColor: CARD_BG, borderRadius: 1 }}>
      <IconButton size="small" sx={{ color: '#666', p: 0.25, cursor: 'grab' }} {...attributes} {...listeners}><DragIndicator fontSize="small" /></IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#fff', fontSize: 12 }}>{name}</Typography>
        <Typography sx={{ color: '#999', fontSize: 10 }}>{artist}</Typography>
      </Box>
      <IconButton size="small" onClick={onRemove} sx={{ color: '#e57373' }}><Close fontSize="small" /></IconButton>
    </Box>
  );
};

export default SpotifyPlaylistGrid;
